import * as React from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { ButtonProps } from '@coursera/cds-core';

import { ExternalCoachAction } from 'bundles/ai-coach-platform/components/patterns/chat/ExternalCoachAction';
import { getCoachPendoTrigger } from 'bundles/ai-course-coach/utils/pendo';
import { useCourseToolBarPlugins } from 'bundles/course-v2/components/navigation/CourseToolBar/useCourseToolBarPlugins';

export type ExternalCoachActionButtonProps = {
  /**
   * The label to display on the button.
   *
   * This will be the message that will be sent to Coach.
   * */
  label: string;

  /**
   * Whether this action button is used within the disclosure component
   * @default false
   */
  isDisclosureButton?: boolean;

  /**
   * Whether this action button triggers the side toolbar open to show Coach chat
   * @default false
   */
  isToolbarTrigger?: boolean;

  /**
   * Whether to use text streaming for chat messages.
   */
  enableTextStreaming?: boolean;

  /**
   * Whether to enable the continuous version of Coach chat
   */
  enableContinuousChat?: boolean;
} & Omit<ButtonProps, 'children'> &
  AiCoachRespondToUserMessageInput;

/**
 * ExternalCoachActionButton component.
 *
 * This component renders a Coach action button that can be used outside of Coach chat and optionally trigger the toolbar open to display the chat.
 * This component lives in ai-course-coach rather than ai-coach-platform because this toolbar is product-specific and only used in ondemand.
 */
export const ExternalCoachActionButton = (props: ExternalCoachActionButtonProps): React.ReactElement => {
  const {
    label,
    isDisclosureButton = false,
    isToolbarTrigger = false,
    variant,
    config,
    courseraContext,
    enableTextStreaming,
    enableContinuousChat,
  } = props;

  const { setToolSelected, setExpanded } = useCourseToolBarPlugins();

  return (
    <ExternalCoachAction
      isDisclosureButton={isDisclosureButton}
      variant={variant}
      label={label}
      message={label}
      config={config}
      courseraContext={courseraContext}
      onClick={() => {
        if (isToolbarTrigger) {
          setToolSelected('coach');
          setExpanded(true);

          // for each message sent, conditionally trigger the pendo guide
          const coachPendoTriggerInstance = getCoachPendoTrigger({});
          coachPendoTriggerInstance.handleNewMessage();
        }
      }}
      enableTextStreaming={enableTextStreaming}
      enableContinuousChat={enableContinuousChat}
    />
  );
};

export default ExternalCoachActionButton;
